import React from 'react';
import { useTranslation } from 'react-i18next';
import { BiCheck, BiInfoCircle } from 'react-icons/bi';
import { FaArrowRight } from 'react-icons/fa6';

import { IGetAllScenariosResponse } from '@/app/services/chatbot-rtk.type';
import { PSButton } from '@/components/ui/button';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import Utilities from '@/utils';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

interface DependencyVisualizationProps {
  selectedBot: {
    bot_id: string;
    name: string;
    type: string;
  };
  dependencies: {
    bot_id: string;
    name: string;
    type: string;
  }[];
  allBots: IGetAllScenariosResponse[];
  onConfirm: () => void;
  onCancel: () => void;
}

const DependencyVisualization: React.FC<DependencyVisualizationProps> = ({ selectedBot, dependencies, allBots, onConfirm, onCancel }) => {
  const { t } = useTranslation();

  const getBotData = (botId: string) => {
    return allBots.find(b => b.id === botId);
  };

  const getBotIcon = (bot: IGetAllScenariosResponse) => {
    if (bot.type === BotTypeEnum.VIDEO_SCENARIO && bot.icon) {
      const icon = bot.icon;
      if (icon.type === 'image') {
        return <img src={icon.value} alt={bot.title} className="tw-w-10 tw-h-10 tw-rounded-full tw-object-cover" />;
      }
      // Handle other icon types if needed
    }
    return (
      <img
        src={bot.logo || 'https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png'}
        alt={bot.title}
        className="tw-w-10 tw-h-10 tw-rounded-full tw-object-cover"
      />
    );
  };

  const selectedBotData = getBotData(selectedBot.bot_id);

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-50">
      <div className="tw-bg-white tw-rounded-xl tw-shadow-lg tw-max-w-2xl tw-w-full tw-mx-4 tw-max-h-[90vh] tw-overflow-y-auto">
        <div className="tw-p-6">
          {/* Header */}
          <div className="tw-flex tw-items-center tw-gap-3 tw-mb-6">
            <BiInfoCircle className="tw-w-6 tw-h-6 tw-text-blue-500" />
            <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-font-['Lexend']">{t('marketplace/step2/dependencies/title')}</h3>
          </div>

          {/* Explanation */}
          <div className="tw-bg-blue-50 tw-p-4 tw-rounded-lg tw-mb-6">
            <p className="tw-text-sm tw-text-blue-800 tw-font-['Lexend']">
              {t('marketplace/step2/dependencies/explanation', {
                botName: selectedBot.name,
                count: dependencies.length,
              })}
            </p>
          </div>

          {/* Dependency Flow Visualization - Grid Layout */}
          <div className="tw-grid tw-grid-cols-1 tw-gap-6">
            {/* Selected Bot - Fixed Size */}
            <div className="tw-grid tw-place-items-center">
              <div className="tw-w-64 tw-h-32 tw-flex tw-flex-col tw-items-center tw-justify-center tw-p-4 tw-bg-blue-50 tw-rounded-xl tw-border-2 tw-border-blue-200">
                {selectedBotData && getBotIcon(selectedBotData)}
                <div className="tw-mt-2 tw-text-center tw-w-full">
                  <div className="tw-font-medium tw-text-gray-900 tw-font-['Lexend'] tw-text-sm tw-truncate tw-px-2">{selectedBot.name}</div>
                  <div className="tw-text-xs tw-text-gray-600 tw-font-['Lexend'] tw-truncate tw-px-2">{Utilities.getBotNameByBotCode(selectedBot.type, t)}</div>
                  <span className="tw-inline-block tw-mt-1 tw-px-2 tw-py-1 tw-bg-blue-100 tw-text-blue-800 tw-text-xs tw-font-medium tw-rounded-full tw-font-['Lexend']">
                    {t('marketplace/step2/dependencies/selected')}
                  </span>
                </div>
              </div>
            </div>

            {/* Arrow and Dependencies Label */}
            {dependencies.length > 0 && (
              <>
                <div className="tw-grid tw-place-items-center">
                  <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                    <FaArrowRight className="tw-w-6 tw-h-6 tw-text-gray-400 tw-transform tw-rotate-90" />
                    <span className="tw-text-sm tw-font-medium tw-text-gray-600 tw-font-['Lexend']">{t('marketplace/step2/dependencies/requires')}</span>
                  </div>
                </div>

                {/* Dependencies Grid */}
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4 tw-justify-items-center">
                  {dependencies.map(dep => {
                    const depBotData = getBotData(dep.bot_id);
                    return (
                      <div
                        key={dep.bot_id}
                        className="tw-w-full tw-max-w-sm tw-flex tw-items-center tw-p-3 tw-bg-green-50 tw-rounded-lg tw-border tw-border-green-200"
                      >
                        <div className="tw-flex tw-items-center tw-gap-3 tw-flex-1">
                          {depBotData && getBotIcon(depBotData)}
                          <div className="tw-flex-1 tw-min-w-0">
                            <div className="tw-font-medium tw-text-gray-900 tw-font-['Lexend'] tw-text-sm tw-truncate">{dep.name}</div>
                            <div className="tw-text-xs tw-text-gray-600 tw-font-['Lexend']">{Utilities.getBotNameByBotCode(dep.type, t)}</div>
                          </div>
                          <BiCheck className="tw-w-5 tw-h-5 tw-text-green-600" />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Summary */}
                <div className="tw-bg-gray-50 tw-p-4 tw-rounded-lg">
                  <div className="tw-flex tw-items-start tw-gap-3">
                    <BiCheck className="tw-w-5 tw-h-5 tw-text-green-600 tw-flex-shrink-0 tw-mt-0.5" />
                    <div>
                      <div className="tw-font-medium tw-text-gray-900 tw-font-['Lexend'] tw-text-sm tw-mb-1">
                        {t('marketplace/step2/dependencies/summary/title')}
                      </div>
                      <div className="tw-text-sm tw-text-gray-600 tw-font-['Lexend']">
                        {t('marketplace/step2/dependencies/summary/description', {
                          total: 1 + dependencies.length,
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Action Buttons */}
          <div className="tw-flex tw-gap-3 tw-mt-6 tw-pt-6 tw-border-t tw-border-gray-200">
            <PSButton id="btn-dependency-cancel" variant="outline" onClick={onCancel} className="tw-flex-1">
              {t('marketplace/step2/dependencies/cancel')}
            </PSButton>
            <PSButton id="btn-dependency-confirm" variant="primary" onClick={onConfirm} className="tw-flex-1">
              {t('marketplace/step2/dependencies/confirm', {
                count: 1 + dependencies.length,
              })}
            </PSButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WrappedErrorBoundary(DependencyVisualization);
